import React, { useState, useMemo, useEffect } from "react";
import payment from "../assets/img/payment-methods.png";
import { useDispatch, useSelector } from "react-redux";
import { selectCartTotalAmount, selectCartProductDetails, selectUserDetails } from "../Selectors/Selector";
import { checkout, createRazorpayOrder } from "../store/thunk/checkout";
import { raiseError, emptyCart } from "../store/actions/action";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { isTokenExpired, getBaseUrl, loadStorage } from "../Utils/Utils";
import axios from "axios";

const STATES = ["Delhi","Uttar Pradesh","Haryana","Punjab","Himachal Pradesh","Rajasthan","Uttarakhand"];

function Checkout() {
    const { productId, quantity } = useParams();
    const { pathname } = useLocation();
    const productDetails = useSelector((store) => store.products.data);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const CartTotalAmount = useSelector(selectCartTotalAmount);
    const userDetails = useSelector(selectUserDetails);

    const [name, setName] = useState((userDetails?.firstName + ' ' + userDetails?.lastName) || '');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [locality, setLocality] = useState('');
    const [state, setState] = useState('');
    const [pinCode, setPinCode] = useState('');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const [pincodeStatus, setPincodeStatus] = useState(null); // { isAvailable, message }
    const [checkingPincode, setCheckingPincode] = useState(false);

    const [couponCode, setCouponCode] = useState('');
    const [couponDiscount, setCouponDiscount] = useState(0);
    const [couponMessage, setCouponMessage] = useState('');
    const [couponError, setCouponError] = useState('');
    const [applyingCoupon, setApplyingCoupon] = useState(false);

    const [savedAddresses, setSavedAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [saveAddress, setSaveAddress] = useState(false);

    const [paymentMethod, setPaymentMethod] = useState('online'); // 'online' | 'cod'
    const [productForCharges, setProductForCharges] = useState(null);

    const cartProductDetails = useSelector(selectCartProductDetails);

    const selectedProductDetails = useMemo(() => {
        if (productDetails?.length > 0 && productId) {
            return productDetails.find((item) => item.id === Number(productId));
        }
        return null;
    }, [productDetails, productId]);

    // Fetch delivery charge info for single-product Buy Now flow
    useEffect(() => {
        if (!productId) return;
        axios.get(`${getBaseUrl()}/products/${productId}`)
            .then(res => setProductForCharges(res.data))
            .catch(() => {});
    }, [productId]);

    const shippingFee = useMemo(() => {
        if (productId) {
            if (!productForCharges) return 0;
            return productForCharges.freeShipping ? 0 : (productForCharges.shippingCharge || 0);
        }
        return (cartProductDetails || []).reduce((sum, item) => {
            const p = productDetails?.find(pd => pd.id === item.productId);
            return sum + (p && !p.freeShipping ? (p.shippingCharge || 0) : 0);
        }, 0);
    }, [productId, productForCharges, cartProductDetails, productDetails]);

    const codFeeAmount = useMemo(() => {
        if (paymentMethod !== 'cod') return 0;
        if (productId) {
            if (!productForCharges) return 0;
            return productForCharges.cashOnDelivery ? 0 : (productForCharges.codCharge || 0);
        }
        return (cartProductDetails || []).reduce((sum, item) => {
            const p = productDetails?.find(pd => pd.id === item.productId);
            return sum + (p && !p.cashOnDelivery ? (p.codCharge || 0) : 0);
        }, 0);
    }, [paymentMethod, productId, productForCharges, cartProductDetails, productDetails]);

    const codAvailable = useMemo(() => {
        if (productId) {
            if (!productForCharges) return false;
            return productForCharges.cashOnDelivery || productForCharges.codCharge > 0;
        }
        return (cartProductDetails || []).some(item => {
            const p = productDetails?.find(pd => pd.id === item.productId);
            return p && (p.cashOnDelivery || p.codCharge > 0);
        });
    }, [productId, productForCharges, cartProductDetails, productDetails]);

    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

    useEffect(() => {
        if (!userDetails) return;
        const token = loadStorage("token");
        axios.get(`${getBaseUrl()}/address`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => {
                setSavedAddresses(res.data);
                const def = res.data.find(a => a.isDefault) || res.data[0];
                if (def) fillFromAddress(def);
            })
            .catch(() => {});
    }, [userDetails]);

    const checkPincodeAvailability = async (pin) => {
        if (pin.length !== 6) { setPincodeStatus(null); return; }
        setCheckingPincode(true);
        try {
            const res = await axios.post(`${getBaseUrl()}/Products/checkPincode?Pincode=${pin}`);
            setPincodeStatus(res.data);
        } catch {
            setPincodeStatus(null);
        } finally {
            setCheckingPincode(false);
        }
    };

    const fillFromAddress = (addr) => {
        setSelectedAddressId(addr.id);
        setName(addr.fullName);
        setPhone(addr.phone);
        setAddress(addr.addressLine);
        setLocality(addr.locality);
        setState(addr.state);
        setPinCode(addr.pincode);
        checkPincodeAvailability(addr.pincode);
    };

    const selectAddress = (addr) => {
        fillFromAddress(addr);
        setSaveAddress(false);
    };

    const useNewAddress = () => {
        setSelectedAddressId(null);
        setName('');
        setPhone('');
        setAddress('');
        setLocality('');
        setState('');
        setPinCode('');
    };

    const validateDetails = () => {
        if (!(selectedProductDetails || cartProductDetails?.length > 0)) return "Cart is empty";
        if (name.length === 0) return "Please enter name.";
        if (phone.length !== 10) return "Please enter valid phone number.";
        if (address.length === 0) return "Please enter address.";
        if (locality.length === 0) return "Please enter locality.";
        if (!state) return "Please select state.";
        if (pinCode.length === 0) return "Please enter pincode.";
        if (pincodeStatus !== null && !pincodeStatus.isAvailable) return "Delivery is not available at this pincode.";
        if (pinCode.length === 6 && pincodeStatus === null) return "Please wait for pincode availability check.";
        return null;
    };

    const buildProductsArray = () => {
        if (productId && quantity && selectedProductDetails) {
            return [{ cartId: 0, ProductId: productId, ProductName: selectedProductDetails.name, Quantity: Number(quantity) }];
        }
        return cartProductDetails.map((item) => ({
            cartId: item.cartId, ProductId: item.productId, ProductName: item.productName, Quantity: item.quantity
        }));
    };

    const placeOrderAfterPayment = (productsArray, totalPrice, razorpayPaymentId, razorpayOrderId, razorpaySignature) => {
        dispatch(checkout({
            UserId: userDetails.id, Name: name, Phone: phone, Address: address,
            Locality: locality, State: state, Pincode: pinCode, TotalAmount: totalPrice,
            Products: productsArray, RazorpayPaymentId: razorpayPaymentId,
            RazorpayOrderId: razorpayOrderId, RazorpaySignature: razorpaySignature,
            PaymentMethod: 'Online',
        })).then((res) => {
            setIsLoading(false);
            if (res.payload) {
                if (!(productId && quantity)) dispatch(emptyCart());
                navigate('/thankYou');
            } else {
                dispatch(raiseError("Order placement failed. Please contact support."));
            }
        });
    };

    const onClickCheckout = async () => {
        if (!userDetails || isTokenExpired()) { dispatch(raiseError("Login is required.")); return; }
        const errorMessage = validateDetails();
        if (errorMessage) { setError(errorMessage); return; }
        setError(null);
        setIsLoading(true);

        // Save new address if checkbox ticked
        if (saveAddress && !selectedAddressId) {
            const token = loadStorage("token");
            axios.post(`${getBaseUrl()}/address`,
                { FullName: name, Phone: phone, AddressLine: address, Locality: locality, State: state, Pincode: pinCode, IsDefault: false },
                { headers: { Authorization: `Bearer ${token}` } }
            ).catch(() => {});
        }

        let productsArray, totalPrice;
        try {
            productsArray = buildProductsArray();
            const base = productId && quantity ? selectedProductDetails.price * Number(quantity) : CartTotalAmount;
            totalPrice = Math.max(0, base - couponDiscount) + shippingFee + codFeeAmount;
        } catch {
            dispatch(raiseError("Something went wrong. please try again later"));
            setIsLoading(false);
            return;
        }

        // COD: place order directly without Razorpay
        if (paymentMethod === 'cod') {
            dispatch(checkout({
                UserId: userDetails.id, Name: name, Phone: phone, Address: address,
                Locality: locality, State: state, Pincode: pinCode, TotalAmount: totalPrice,
                Products: productsArray, PaymentMethod: 'COD',
            })).then((res) => {
                setIsLoading(false);
                if (res.payload) {
                    if (!(productId && quantity)) dispatch(emptyCart());
                    navigate('/thankYou');
                } else {
                    dispatch(raiseError("Order placement failed. Please contact support."));
                }
            });
            return;
        }

        const razorpayData = await dispatch(createRazorpayOrder(totalPrice)).unwrap().catch(() => null);
        if (!razorpayData) { setIsLoading(false); return; }

        const options = {
            key: razorpayData.keyId,
            amount: razorpayData.amount * 100,
            currency: "INR",
            name: "Coliza",
            description: "Medicine Order",
            order_id: razorpayData.razorpayOrderId,
            prefill: { name, contact: phone },
            theme: { color: "#4E97FD" },
            handler: (response) => placeOrderAfterPayment(productsArray, totalPrice,
                response.razorpay_payment_id, response.razorpay_order_id, response.razorpay_signature),
            modal: { ondismiss: () => setIsLoading(false) }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (response) => {
            setIsLoading(false);
            dispatch(raiseError(`Payment failed: ${response.error.description}`));
        });
        rzp.open();
    };

    const baseTotal = selectedProductDetails ? selectedProductDetails.price * Number(quantity) : CartTotalAmount;
    const total = Math.max(0, baseTotal - couponDiscount) + shippingFee + codFeeAmount;
    const gst = Math.round(total * 0.18 / 1.18 * 100) / 100;
    const subTotal = Math.round((total - gst) * 100) / 100;

    const applyCoupon = async () => {
        if (!couponCode.trim()) return;
        setApplyingCoupon(true); setCouponError(''); setCouponMessage('');
        try {
            const token = loadStorage("token");
            const res = await axios.post(`${getBaseUrl()}/coupon/validate`,
                { Code: couponCode.trim(), OrderAmount: baseTotal },
                { headers: { Authorization: `Bearer ${token}` } });
            setCouponDiscount(res.data.discount);
            setCouponMessage(res.data.message);
        } catch (err) {
            setCouponDiscount(0);
            setCouponError(err.response?.data?.message || "Invalid coupon.");
        } finally { setApplyingCoupon(false); }
    };

    return (
        <section className="section-padding mt-5">
            <div className="container">
                <div className="row justify-content-between">
                    {error && <p style={{ color: "red" }}>{error}</p>}
                    <div className="col-md-9">

                        {/* Saved address cards */}
                        {savedAddresses.length > 0 && (
                            <div className="mb-3">
                                <h6 className="font-weight-bold mb-2">Deliver to a saved address</h6>
                                <div className="row">
                                    {savedAddresses.map(addr => (
                                        <div className="col-sm-6 col-lg-4 mb-2" key={addr.id}>
                                            <div
                                                className={`card h-100 cursor-pointer ${selectedAddressId === addr.id ? 'border-primary' : ''}`}
                                                style={{ cursor: 'pointer', transition: 'border 0.15s' }}
                                                onClick={() => selectAddress(addr)}
                                            >
                                                <div className="card-body p-2" style={{ fontSize: '0.82rem' }}>
                                                    {selectedAddressId === addr.id && (
                                                        <i className="fa fa-check-circle text-primary float-right"></i>
                                                    )}
                                                    {addr.isDefault && <span className="badge bg-primary mb-1" style={{ fontSize: '0.7rem' }}>Default</span>}
                                                    <p className="mb-0 font-weight-bold">{addr.fullName}</p>
                                                    <p className="mb-0 text-muted">{addr.addressLine}</p>
                                                    <p className="mb-0 text-muted">{addr.locality}, {addr.state}</p>
                                                    <p className="mb-0 text-muted">{addr.pincode}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <div className="col-sm-6 col-lg-4 mb-2">
                                        <div
                                            className={`card h-100 ${selectedAddressId === null ? 'border-primary' : ''}`}
                                            style={{ cursor: 'pointer', minHeight: 80 }}
                                            onClick={useNewAddress}
                                        >
                                            <div className="card-body p-2 d-flex align-items-center justify-content-center text-center">
                                                <div>
                                                    <i className="ti-plus text-primary" style={{ fontSize: '1.2rem' }}></i>
                                                    <p className="mb-0 small mt-1">Use a new address</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <hr />
                            </div>
                        )}

                        {/* Address form */}
                        <div className="card">
                            <div className="card-body">
                                <h6 className="font-weight-bold mb-3">
                                    {selectedAddressId ? 'Delivery Details' : 'Enter Delivery Address'}
                                </h6>
                                <div className="row pb-2">
                                    <div className="col-sm-6">
                                        <div className="form-group mb-4">
                                            <label className="form-label">Name <span className="text-danger">*</span></label>
                                            <input className="form-control" type="text" value={name} onChange={(e) => setName(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="form-group mb-4">
                                            <label className="form-label">Phone <span className="text-danger">*</span></label>
                                            <input className="form-control" type="text" value={phone} onChange={(e) => setPhone(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="form-group mb-4">
                                            <label className="form-label">Address</label>
                                            <input className="form-control" type="text" value={address} onChange={(e) => setAddress(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="form-group mb-4">
                                            <label className="form-label">Locality/Town</label>
                                            <input className="form-control" type="text" value={locality} onChange={(e) => setLocality(e.target.value)} />
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="form-group mb-4">
                                            <label className="form-label">State <span className="text-danger">*</span></label>
                                            <select className="form-control" value={state} onChange={(e) => setState(e.target.value)}>
                                                <option value="">Select state</option>
                                                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-sm-6">
                                        <div className="form-group mb-4">
                                            <label className="form-label">Pin Code <span className="text-danger">*</span></label>
                                            <input
                                                className={`form-control ${pincodeStatus ? (pincodeStatus.isAvailable ? 'is-valid' : 'is-invalid') : ''}`}
                                                type="text"
                                                maxLength={6}
                                                value={pinCode}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '');
                                                    setPinCode(val);
                                                    setPincodeStatus(null);
                                                    checkPincodeAvailability(val);
                                                }}
                                            />
                                            {checkingPincode && <small className="text-muted">Checking availability...</small>}
                                            {!checkingPincode && pincodeStatus && (
                                                <small className={pincodeStatus.isAvailable ? 'text-success' : 'text-danger'}>
                                                    {pincodeStatus.isAvailable ? '✓' : '✗'} {pincodeStatus.message}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                    {!selectedAddressId && (
                                        <div className="col-12">
                                            <div className="form-check mb-2">
                                                <input className="form-check-input" type="checkbox" id="saveAddr"
                                                    checked={saveAddress} onChange={e => setSaveAddress(e.target.checked)} />
                                                <label className="form-check-label" htmlFor="saveAddr">
                                                    Save this address for future orders
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-3 mt-lg-0 mt-6">
                        <div className="cart-summary">
                            <div className="cart-summary-wrap">
                                <h4>Cart Summary</h4>
                                <p>Sub Total (excl. GST) <span>₹{subTotal}</span></p>
                                <p>GST (18%) <span>₹{gst}</span></p>
                                {shippingFee > 0
                                    ? <p>Shipping Charge <span>₹{shippingFee.toFixed(2)}</span></p>
                                    : <p>Shipping Cost <span className="text-success">Free</span></p>
                                }
                                {paymentMethod === 'cod' && codFeeAmount > 0 && (
                                    <p>COD Charge <span>₹{codFeeAmount.toFixed(2)}</span></p>
                                )}
                                {couponDiscount > 0 && (
                                    <p className="text-success">Coupon Discount <span>-₹{couponDiscount}</span></p>
                                )}
                                <h2>Grand Total <span>₹{total.toFixed(2)}</span></h2>
                            </div>
                            <div className="mt-3">
                                <label className="form-label small font-weight-bold">Have a coupon?</label>
                                <div className="input-group input-group-sm mb-1">
                                    <input type="text" className="form-control" placeholder="Enter coupon code"
                                        value={couponCode}
                                        onChange={(e) => { setCouponCode(e.target.value); setCouponDiscount(0); setCouponMessage(''); setCouponError(''); }}
                                    />
                                    <button className="btn btn-outline-secondary" onClick={applyCoupon} disabled={applyingCoupon}>
                                        {applyingCoupon ? '...' : 'Apply'}
                                    </button>
                                </div>
                                {couponMessage && <small className="text-success">{couponMessage}</small>}
                                {couponError && <small className="text-danger">{couponError}</small>}
                            </div>
                            <div className="mt-3">
                                <label className="form-label small font-weight-bold">Payment Method</label>
                                <div className="mb-2">
                                    <div className="form-check">
                                        <input className="form-check-input" type="radio" id="payOnline" name="payMethod"
                                            checked={paymentMethod === 'online'} onChange={() => setPaymentMethod('online')} />
                                        <label className="form-check-label small" htmlFor="payOnline">Online (UPI / Card / Net Banking)</label>
                                    </div>
                                    {codAvailable && (
                                        <div className="form-check">
                                            <input className="form-check-input" type="radio" id="payCOD" name="payMethod"
                                                checked={paymentMethod === 'cod'} onChange={() => setPaymentMethod('cod')} />
                                            <label className="form-check-label small" htmlFor="payCOD">
                                                Cash on Delivery
                                                {productForCharges && !productForCharges.cashOnDelivery && productForCharges.codCharge > 0 && (
                                                    <span className="text-muted"> (+₹{productForCharges.codCharge} charge)</span>
                                                )}
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="mt-2">
                                {paymentMethod === 'online' && <img src={payment} alt="payment methods" className="img-fluid mb-3" />}
                                <button className="btn btn-primary btn-full btn-medium rounded" type="button"
                                    disabled={isLoading || checkingPincode || (pincodeStatus !== null && !pincodeStatus.isAvailable)} onClick={onClickCheckout}>
                                    {isLoading && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                                    {isLoading ? 'Processing...' : paymentMethod === 'cod' ? 'Place Order (COD)' : 'Pay & Place Order'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Checkout;
